import { day, ensure, hasValue } from '@vrplatform/utils';
import type { listing_bool_exp } from '@vrplatform/graphql';
import {
  whereGlListingIsInactive,
  whereListingIsActive,
} from '@vrplatform/graphql';
import { z } from 'zod';
import type { Dashboard } from '../types';
import { whereAddress } from './whereAddress';
import { whereLegacyListingIsExcluded } from './whereLegacyListingIsExcluded';
import { whereListingPeriod } from '.';

export const listingsFilterInput = {
  search: z.string().nullish(),
  ownerIds: z.array(z.string().nullish()).nullish(),
  status: z.enum(['inactive', 'active', 'all']).nullish(),
  collectionId: z.string().nullish(),
  pmsConnectionId: z.string().nullish(),
  disabledByAutomationId: z.union([z.string(), z.array(z.string())]).nullish(),
};

const listingsFilter = z.object(listingsFilterInput);

export type ListingsFilterParams = z.infer<typeof listingsFilter>;

type BaseWhere = {
  dashboard: Dashboard;
  tenantId: string;
  ownerPortalContactId: string | null;
  GL: boolean;
};

export function whereListings({
  search: s,
  dashboard,
  tenantId,
  disabledByAutomationId,
  status,
  ownerIds: oids,
  ownerPortalContactId,
  collectionId,
  pmsConnectionId,
  GL,
}: BaseWhere & ListingsFilterParams) {
  const search = s?.trim();

  const searchBool = search
    ? ensure<listing_bool_exp[]>([
        {
          address: { _ilike: `%${search}%` },
        },
        {
          uniqueRef: { _ilike: `%${search}%` },
        },
        {
          calculated_title: { _ilike: `%${search}%` },
        },
        {
          ownerships: {
            newOwner: {
              _or: [
                { firstName: { _ilike: `%${search}%` } },
                { name: { _ilike: `%${search}%` } },
              ],
            },
          },
        },
        {
          connections: {
            name: { _ilike: `%${search}%` },
          },
        },
        {
          addressRelation: whereAddress({ search }),
        },
        {
          addressData: {
            _cast: {
              String: {
                _ilike: `%${search}%`,
              },
            },
          },
        },
      ])
    : undefined;

  const statusBool: listing_bool_exp | undefined =
    status === 'inactive'
      ? GL
        ? whereGlListingIsInactive(undefined, day().yyyymmdd())
        : {
            _or: [
              {
                status: { _eq: 'inactive' },
              },
              {
                pmsStatus: { _eq: 'inactive' },
                status: { _is_null: true },
              },
            ],
          }
      : GL && status === 'all'
        ? undefined
        : {
            _or: [whereListingIsActive()],
          };

  const ownerIds = oids?.filter(hasValue);

  return ensure<listing_bool_exp>({
    tenantId: { _eq: tenantId },
    collectionId: collectionId
      ? collectionId === 'null'
        ? {
            _is_null: true,
          }
        : { _eq: collectionId }
      : undefined,
    ownerships: GL
      ? undefined
      : ownerIds?.length
        ? {
            newOwnerId: { _in: ownerIds },
          }
        : dashboard === 'owner'
          ? {
              newOwner: {
                userAccesses: {
                  _or: [
                    {
                      ownerId: { _eq: ownerPortalContactId },
                    },
                  ],
                },
              },
            }
          : undefined,
    ownershipPeriods:
      GL && (ownerIds?.length || ownerPortalContactId)
        ? ownerPortalContactId
          ? {
              members: {
                contactId: {
                  _eq: ownerPortalContactId,
                },
              },
              setListingInactive: {
                _eq: false,
              },
            }
          : whereListingPeriod({
              date: day().yyyymmdd(),
              ownerIds,
              type: 'ownership',
            })
        : undefined,
    connectionId: pmsConnectionId ? { _eq: pmsConnectionId } : undefined,
    _and: disabledByAutomationId?.length
      ? [whereLegacyListingIsExcluded(disabledByAutomationId)]
      : statusBool
        ? [statusBool]
        : undefined,
    _or: searchBool,
  });
}
